import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import orellaOIcon from '../assets/logos/orella_o_icon.png'

/**
 * DeleteCaseModal — "Leave this case for now?" sheet.
 *
 * s124 (Dr. Rami, TestFlight build 90: "Lets redesign this screen ux to be more orella design consistent"; canvas
 * "Orella Case Home — Revised" board D and copy doc ORELLA_CASE_HOME_COPY_v1 §4 APPROVED 2026-10-30): a white bottom
 * sheet with the Orella O, one navy primary ("Save draft & leave"), "Keep working" second, and "Delete this case" as a
 * quiet red link that asks once more before it deletes. Was: a navy header with an amber warning tile and two large
 * coloured option cards, delete as loud as save.
 *
 * Props (unchanged for every caller):
 * - isOpen, onClose, onDelete, onSaveAsDraft
 * - caseName: string (optional) — the case's focus area
 * - canDelete: boolean (default true) — ★ PROD-416: false for a draft a physician holds
 *   (utils/caseDelete.js isHeldByDoctor); no delete is offered at all
 * - progress: { done, total } (optional) — answered questions, for "You've answered 6 of 10 questions."
 */
export default function DeleteCaseModal({
  isOpen,
  onClose,
  onDelete,
  onSaveAsDraft,
  caseName = '',
  canDelete = true,
  progress = null,
}) {
  const { t } = useI18n()
  const [confirming, setConfirming] = useState(false)
  useEffect(() => { if (!isOpen) setConfirming(false) }, [isOpen])

  if (!isOpen) return null

  const counted = progress && Number(progress.total) > 0 && Number(progress.done) >= 0
  const body = counted
    ? t('caseHome.leaveBodyCount', { defaultValue: "You've answered {{done}} of {{total}} questions. Save it and I'll pick up right where you left off.", done: progress.done, total: progress.total })
    : t('caseHome.leaveBody', { defaultValue: "Save it and I'll pick up right where you left off." })

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
         style={{ background: 'rgba(12,30,46,0.55)' }} onClick={onClose}>
      <div
        role="dialog" aria-modal="true" aria-labelledby="leave-case-title"
        className="bg-white rounded-t-[28px] sm:rounded-[24px] w-full sm:max-w-md overflow-hidden animate-slide-up bottom-actions [--ba-min:0px]"
        style={{ boxShadow: '0 -8px 30px rgba(12,30,46,0.25)', color: '#15354F' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-2.5 pb-5 flex flex-col" style={{ gap: 16 }}>
          <div className="w-10 h-[5px] rounded-full self-center" style={{ background: '#D8E2EA' }} />
          <div className="flex justify-end" style={{ marginTop: -6, marginBottom: -26 }}>
            <button onClick={onClose} aria-label={t('common.close', 'Close')}
                    className="w-11 h-11 rounded-full flex items-center justify-center border-0"
                    style={{ background: '#F1F5F9', color: '#475569' }}>
              <X size={18} />
            </button>
          </div>

          <div className="flex flex-col items-center text-center" style={{ gap: 10 }}>
            <img src={orellaOIcon} alt="" style={{ width: 52, height: 52 }} />
            <h3 id="leave-case-title" className="m-0 font-extrabold" style={{ fontSize: 23 }}>
              {t('caseHome.leaveTitle', 'Leave this case for now?')}
            </h3>
            <p className="m-0" style={{ fontSize: 15, lineHeight: 1.5, color: '#475569', maxWidth: 310 }}>{body}</p>
          </div>

          {caseName && (
            <div className="flex items-center gap-3 rounded-2xl px-3.5 py-3" style={{ background: '#F5F9FD', border: '1px solid #E3ECF3' }}>
              <span className="flex-1 min-w-0 text-left">
                <span className="block text-[12px] font-semibold" style={{ color: '#475569' }}>{t('deleteModal.caseLabel', 'Current case:')}</span>
                <span className="block text-[15px] font-bold truncate">{caseName}</span>
              </span>
              <span className="text-[11px] font-bold rounded-md px-2 py-[3px] flex-shrink-0" style={{ color: '#8A5300', background: '#FDF1DC' }}>
                {t('caseHome.statusDraft', 'Draft').toUpperCase()}
              </span>
            </div>
          )}

          <div className="flex flex-col" style={{ gap: 10 }}>
            <button onClick={onSaveAsDraft} className="w-full rounded-full border-0 font-bold"
                    style={{ background: '#15354F', color: '#fff', padding: 15, fontSize: 15 }}>
              {t('caseHome.saveDraftLeave', 'Save draft & leave')}
            </button>
            <button onClick={onClose} className="w-full rounded-full font-bold"
                    style={{ background: '#fff', color: '#15354F', border: '1.5px solid #D8E6F0', padding: 13, fontSize: 15 }}>
              {t('caseHome.keepWorking', 'Keep working')}
            </button>

            {/* PROD-416: never on a draft a physician holds. One tap asks; the second deletes. */}
            {canDelete && (confirming ? (
              <div className="rounded-2xl p-3.5 flex flex-col" style={{ gap: 10, background: '#FEF3F2', border: '1px solid #FECDCA' }}>
                <p className="m-0 text-center text-[14px]" style={{ color: '#7A271A' }}>
                  {t('caseHome.deleteCaseConfirm', 'Are you sure you want to delete this case? This action cannot be undone.')}
                </p>
                <div className="flex" style={{ gap: 8 }}>
                  <button onClick={() => setConfirming(false)} className="flex-1 rounded-full font-bold"
                          style={{ background: '#fff', color: '#15354F', border: '1.5px solid #D8E6F0', padding: 11, fontSize: 14 }}>
                    {t('common.cancel', 'Cancel')}
                  </button>
                  <button onClick={onDelete} className="flex-1 rounded-full border-0 font-bold flex items-center justify-center gap-1.5"
                          style={{ background: '#B42318', color: '#fff', padding: 11, fontSize: 14 }}>
                    <Trash2 size={15} />
                    {t('caseHome.deleteCase', 'Delete Case')}
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirming(true)} className="w-full rounded-full border-0 font-bold flex items-center justify-center gap-1.5"
                      style={{ background: 'transparent', color: '#B42318', padding: 12, fontSize: 14 }}>
                <Trash2 size={16} />
                {t('caseHome.deleteThisCase', 'Delete this case')}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
